import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLeaderboard, useSeasons } from '../api/hooks';
import { ErrorView, EmptyView } from '../components/StateViews';
import { haptics } from '../telegram';
import { track } from '../analytics';
import { seasonChapter } from '../format';
import { TrophyIcon } from '../components/icons';
import type { LeaderboardRow } from '../api/types';

const SEASONS_PER_CHAPTER = 10;

function Row({ r, label }: { r: LeaderboardRow; label?: string }) {
  // A fresh season ranks everyone #1 with 0 points — no crowns until scored.
  const leader = r.rank === 1 && r.points > 0;
  return (
    <Link
      className={`lb-row ${r.isMe ? 'me' : ''} ${leader ? 'first' : ''}`}
      to={`/players/${r.playerId}`}
      onClick={() => track('player_open', { playerId: r.playerId, from: 'leaderboard' })}
    >
      <div className="lb-rank">{r.rank}</div>
      <div className="lb-name">
        {label ?? r.name}
        {leader ? ' ♛' : ''}
      </div>
      <div className="lb-points">{r.points}</div>
    </Link>
  );
}

/** R1 «Главы + сезоны»: chapter stepper ‹ › + up to 10 season tiles per chapter
 *  (numbering resets each chapter; the absolute id stays internal). */
export function LeaderboardScreen() {
  const seasons = useSeasons();
  const [seasonId, setSeasonId] = useState<number | null>(null);
  const [chapter, setChapter] = useState<number | null>(null);

  // Seasons that actually have games, by absolute id.
  const played = useMemo(() => new Set((seasons.data ?? []).map((s) => s.id)), [seasons.data]);
  const maxChapter = useMemo(
    () => (seasons.data?.length ? Math.max(...seasons.data.map((s) => s.chapter)) : 1),
    [seasons.data],
  );

  useEffect(() => {
    if (seasonId === null && seasons.data && seasons.data.length > 0) {
      const active = seasons.data.find((s) => s.isActive) ?? seasons.data[0];
      setSeasonId(active.id);
      setChapter(active.chapter);
    }
  }, [seasons.data, seasonId]);

  const lb = useLeaderboard(seasonId);

  if (seasons.isError) return <ErrorView message={(seasons.error as Error)?.message} />;
  if (chapter === null || seasonId === null) return <LeaderboardSkeleton />;

  const seasonInChapter = seasonId !== null ? ((seasonId - 1) % SEASONS_PER_CHAPTER) + 1 : 1;
  const selectedInThisChapter = seasonChapter(seasonId) === chapter;

  const stepChapter = (delta: number) => {
    const next = chapter + delta;
    if (next < 1 || next > maxChapter) return;
    haptics.tap();
    track('season_switch', { chapter: next });
    setChapter(next);
    // Jump selection to the latest played season of that chapter.
    const inChapter = (seasons.data ?? []).filter((s) => s.chapter === next);
    if (inChapter.length) setSeasonId(Math.max(...inChapter.map((s) => s.id)));
  };

  const pickSeason = (k: number) => {
    const abs = (chapter - 1) * SEASONS_PER_CHAPTER + k;
    if (!played.has(abs)) return;
    haptics.tap();
    track('season_switch', { seasonId: abs, chapter });
    setSeasonId(abs);
  };

  return (
    <div>
      <div className="screen-title-row">
        <div className="screen-title">Таблица лидеров</div>
        <Link
          className="iconbtn iconbtn--gold"
          to="/hall-of-fame"
          aria-label="Зал славы"
          onClick={() => {
            haptics.tap();
            track('hall_of_fame_open', { from: 'leaderboard' });
          }}
        >
          <TrophyIcon />
        </Link>
      </div>

      <div className="chapter-stepper">
        <button
          className="step"
          onClick={() => stepChapter(-1)}
          disabled={chapter <= 1}
          aria-label="Предыдущая глава"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4}>
            <path d="M15 5l-7 7 7 7" />
          </svg>
        </button>
        <div className="mid">
          <div className="ch">Глава {chapter}</div>
          <div className="sub">
            {selectedInThisChapter ? `СЕЗОН ${seasonInChapter} ИЗ ${SEASONS_PER_CHAPTER}` : `СЕЗОНОВ: ${SEASONS_PER_CHAPTER}`}
          </div>
        </div>
        <button
          className="step step--fwd"
          onClick={() => stepChapter(1)}
          disabled={chapter >= maxChapter}
          aria-label="Следующая глава"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4}>
            <path d="M9 6l6 6-6 6" />
          </svg>
        </button>
      </div>

      <div className="season-tiles">
        {Array.from({ length: SEASONS_PER_CHAPTER }, (_, i) => {
          const k = i + 1;
          const abs = (chapter - 1) * SEASONS_PER_CHAPTER + k;
          const isCurrent = abs === seasonId;
          const isPlayed = played.has(abs);
          return (
            <button
              key={k}
              className={`tile ${isCurrent ? 'tile--current' : isPlayed ? '' : 'tile--future'}`}
              disabled={!isPlayed}
              onClick={() => pickSeason(k)}
            >
              {k}
            </button>
          );
        })}
      </div>

      <div className="lb-head">
        <span>Место · Игрок</span>
        <span>Очки</span>
      </div>

      {lb.isLoading ? (
        <LeaderboardSkeleton rowsOnly />
      ) : lb.isError ? (
        <ErrorView message={(lb.error as Error)?.message} />
      ) : !lb.data || lb.data.rows.length === 0 ? (
        <EmptyView message="В этом сезоне ещё нет результатов" />
      ) : (
        <>
          <div className="lb-list">
            {lb.data.rows.map((r) => (
              <Row key={r.playerId} r={r} />
            ))}
          </div>
          {lb.data.me && (
            // key: Telegram-вебвью (WebKit) перерисовывает sticky-слой лениво —
            // без пересоздания узла место обновлялось только при прокрутке.
            <div className="me-sticky you-row" key={`me-${seasonId}`}>
              <Row r={lb.data.me} label={`Вы · ${lb.data.me.name}`} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** Shimmer placeholders (handoff: skeleton rows while loading). */
function LeaderboardSkeleton({ rowsOnly = false }: { rowsOnly?: boolean }) {
  return (
    <div>
      {!rowsOnly && (
        <>
          <div className="skeleton" style={{ height: 34, borderRadius: 10, margin: '4px 0 13px' }} />
          <div className="skeleton" style={{ height: 62, borderRadius: 9, marginBottom: 12 }} />
        </>
      )}
      {Array.from({ length: 8 }, (_, i) => (
        <div key={i} className="skeleton" style={{ height: 34, borderRadius: 10, marginBottom: 8 }} />
      ))}
    </div>
  );
}
